'use client';

import React from 'react';

import { Button, Card, Heading, Input } from '@ui';
import { Container, Section } from '@layout';
import { withOptionalId } from '@lib/withOptionalId';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

interface ContactFlowCompositionProps {
  sectionID?: string;
  classes?: string;
  cardClassName?: string;
  isSpanish?: boolean;
}

const emailOk = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const ContactFlowComposition: React.FC<ContactFlowCompositionProps> = ({
  isSpanish = false,
  sectionID = 'contact-flow-pattern',
  classes = '',
  cardClassName,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const [success, setSuccess] = React.useState(false);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container>
        <Heading
          as='h3'
          title={t('Pattern: contact & validation', 'Patron: contacto y validacion')}
          description={t(
            'A composed slice—input, primary action, inline outcome, and success feedback—shows how tokens and components work together beyond isolated controls.',
            'Un fragmento compuesto—entrada, accion principal, resultado en linea y confirmacion—muestra como tokens y componentes encajan mas alla de controles aislados.'
          )}
        />

        <Card className={`${cardClassName}`}>
          {success ? (
            <div className='pattern-composition__outcome' role='status'>
              <p className='sub' style={{ margin: 0 }}>
                {t(
                  'Thanks — this demo does not send email. In a product you would show confirmation from your API or redirect here.',
                  'Gracias — esta demo no envia correo. En un producto mostrarias la confirmacion de tu API o redirigirias aqui.'
                )}
              </p>
              <div className='row btn-row gap' style={{ flexWrap: 'wrap', marginTop: '1.2rem' }}>
                <Button
                  type='button'
                  className='btn-secondary'
                  label={t('Try again', 'Probar de nuevo')}
                  onClick={() => setSuccess(false)}
                />
              </div>
            </div>
          ) : (
            <form
              className='pattern-composition__form'
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new FormData(form);
                const raw = String(data.get('email-demo') ?? '');
                if (!emailOk(raw)) return;
                setSuccess(true);
              }}
            >
              <Input
                id='contact-email-demo'
                label={t('Work email', 'Correo de trabajo')}
                name='email-demo'
                type='email'
                placeholder='you@company.com'
                autoComplete='email'
                required
              />
              <div className='row btn-row gap' style={{ flexWrap: 'wrap' }}>
                <Button
                  type='submit'
                  label={t('Request access', 'Solicitar acceso')}
                  className='btn-primary'
                />
              </div>
              <p className='small' style={{ color: 'var(--muted)', margin: 0 }}>
                {t(
                  'Submit runs validation; async checks are demonstrated in the inline validation sections above.',
                  'Enviar ejecuta validacion; las comprobaciones asincronas estan en las secciones de validacion anteriores.'
                )}
              </p>
            </form>
          )}
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<form class="card">
  <label for="name">Name</label>
  <input id="name" type="text" />
  <button class="btn btn-primary" type="submit"><span class="label">Send</span></button>
</form>`}
        />
      </Container>
    </Section>
  );
};

export default ContactFlowComposition;
